export type TrackPageLoaded = {
  language: string;
  pageId: string;
  pageTitle: string;
};

export enum MessageType {
  error = '1',
  success = '2',
  warning = '3',
  info = '4',
}

export type TrackFormBase = { formOrWfId: string; formSectionName?: string };

export type TrackFormFieldInteraction = TrackFormBase & {
  fieldElmId: string;
  fieldLabel?: string;
};

export type TrackFormFieldError = {
  fieldElmId: string;
  error: string;
  type?: string;
};

export type TrackMessage = {
  messageId: string;
  messageType: MessageType;
  messageDescription: string;
};

export type TrackModalOpen = {
  wireframeId: string;
  heading?: string;
  variant?: string;
};

export enum VerificationMethod {
  DIO = 'DIO',
  BankID = 'BankID',
  Carrier = 'Carrier',
}

export enum ModalAction {
  verificationMethodSelected = 'verificationMethodSelected',
}

export type TrackModalAction = TrackModalOpen & {
  action: ModalAction;
  actionType: VerificationMethod;
};

export enum OfferSource {
  Exponea = 'Exponea',
  Unica = 'Unica',
}

export type TrackAddToCart = {
  id: string;
  offerSourceType?: OfferSource;
  unicaAudienceId?: string;
  unicaTreatmentCode?: string;
  unicaAudienceIdType?: string;
  onlineOfferCode?: string;
};

export type Vas = {
  id: string;
  name: string;
  category: string;
  price: { invoice?: number; upfront?: number; monthly?: number };
};

export enum DiscountType {
  ABSOLUTE = 'abs',
  RELATIVE = 'rel',
}

export type Discount = {
  id: string;
  displayName: string;
  category: string;
  code: string;
  discountType: DiscountType;
  discount: number;
};

export type Product = {
  id: string;
  name: string;
  category: string;
  price: number;
  commitment?: number;
  campaignOfferId?: string;
  serviceNumber?: string;
  items?: Vas[];
  vouchers?: Discount[];
  actionType?: FastOrderType;
};

export enum PaymentType {
  COD = 'Dobírka',
}

export enum PostponedProcessing {
  AS_SOON_AS_POSSIBLE = 'AS_SOON_AS_POSSIBLE',
  ON_BILL_CYCLE_END = 'ON_BILL_CYCLE_END',
}

export enum DeliveryMethod {
  DELIVERY_METHOD_CURRIER = 'DELIVERY_METHOD_CURRIER',
  DELIVERY_METHOD_POST = 'DELIVERY_METHOD_POST',
  DELIVERY_METHOD_ONLINE = 'DELIVERY_METHOD_ONLINE',
}

export enum LeadChannel {
  CRM = 'CRM',
  DIGI_BUNDLING = 'DigiBundling',
}

export const enum OCpaymentMethod {
  CASH = 'CASH',
  PAYMENT_CARD = 'PAYMENT_CARD',
  BANK_TRANSFER = 'BANK_TRANSFER',
  CASH_ON_DELIVERY = 'CASH_ON_DELIVERY',
  SAP_INVOICE = 'SAP_INVOICE',
}

export type Order = {
  orderRefNo?: string;
  registrationEmail?: string;
  priceOverview?: {
    invoice?: number;
    upfront?: number;
    monthly?: number;
  };
  deliveryMethod: DeliveryMethod;
  /** including VAT */
  deliveryPrice: number;
  postponedProcessing: PostponedProcessing;
  paymentType?: PaymentType;
  /** including VAT */
  paymentPrice?: number;
  customerAccountId: string;
  verificationMethod?: VerificationMethod;
  /** no login - only what external user sees */
  createdBy?: string;
  channel: LeadChannel;

  OCpaymentMethod?: OCpaymentMethod;
  OCupfront?: number;
  OCupfrontNoVat?: number;
  forFree?: boolean;
  priceMonthly?: number;
  priceMonthlyNoVat?: number;
  priceQuarterly?: number;
  priceYearly?: number;
  unknownPrice?: any;
};

export type TrackOrderSubmitSuccess = {
  order: Order;
  products: Product[];
};

export type WireframeId = string;

export type WireframeElementId = string;

export type Offer = {
  offerName?: string;
  offerPosition?: number;
  offerSourceType: OfferSource;
  offerTreatmentCode?: string;
  onlineOfferCode: string;
  unicaAudienceId?: string;
  unicaAudienceIdType?: number;
  unicaSlotCode: string;
  // @deprecated use offerTreatmentCode
  unicaTreatmentCode?: string;
};

/**
 * Type derived from customer-bff
 */
export enum CustomerSegment {
  A = 'A',
  R = 'R',
  SMB = 'SMB',
  CORP = 'CORP',
  GOV = 'GOV',
  OLO = 'OLO',
}

/**
 * Type derived from customer-bff
 */
export enum CustomerSubjectType {
  Person = 'Person',
  Foreigner = 'Foreigner',
  Business = 'Business',
  Entrepreneur = 'Entrepreneur',
  UnivSubject = 'UnivSubject',
  Org = 'Org',
  Unidentified = 'Unidentified',
}

/**
 * Type derived from customer-bff
 */
export interface PrimaryExternalUser {
  email?: string;
  phoneNoMobile?: string;
}

/**
 * Type derived from customer-bff
 */
export interface CustomerInfo {
  cuRefNo: string;
  displayName?: string;
  segment?: CustomerSegment;
  subjectType?: CustomerSubjectType;
  customerCareLevel?: string;
  marketingAgreement?: boolean;
  primaryAdmin?: PrimaryExternalUser;
}

/**
 * Type derived from customer-bff
 */
export interface ExternalUserBasic {
  /** Login of external user. */
  login?: string;
  /** First name of external user. */
  firstName?: string;
  /** Last name of external user. */
  lastName?: string;
  /** Mobile phone of external user. */
  phoneNoMobile?: string;
}

/**
 * Type derived from user-access-bff
 */
export interface EntityAccess {
  /** Type of role (E110, E210, ...). According to role type in acl matrix */
  roleType?: string;
  /** Reference number of customer in which tree is access assigned. */
  cuRefNo?: string;
  /** Information that access is trusted. */
  isTrusted?: boolean;
}

/**
 * Type derived from user-bff
 */
export type ExternalUserDetailAllOf = {
  /** List of accesses to entities */
  entityAccesses?: EntityAccess[];
};

/**
 * Type derived from user-bff
 */
export type ExternalUserDetail = ExternalUserBasic & ExternalUserDetailAllOf;

export type TrackTabChange = {
  wfElementId: string;
  tabName: string;
};

export enum CallbackType {
  online = 'online',
  offline = 'offline',
  busy = 'busy',
}

export enum CmbCategory {
  // CMB je součástí stránky
  standard = 'standard',
  // CMB vyskakuje po určitém časovém úseku stráveném na stránce jako overlay
  proactive = 'proactive',
  // CMB je umístěn v modálním okně, k jeho otevření je nutné uživatelská akce
  modal = 'modal',
  // Deeplink
  deeplink = 'deeplink',
  // Cokoliv neodpovídá předchozím
  other = 'other',
}

export enum InsertType {
  'success-genesys' = 'success-genesys',
  async = 'async',
}

export interface Lead {
  wireframeId: WireframeId;
  cmbId: number;
  cmbCategory: CmbCategory;
  cmbHeadline: string;
  msisdn: string;
  insertType?: InsertType;
  callbackType?: CallbackType;
  campaignName?: string;
  iframeParentWindowUrl?: string;
  hodaLeadId?: string;
  isColdLeadOverwrite?: boolean;
}

export enum FastOrderType {
  activation = 'activation',
  deactivation = 'deactivation',
  change = 'change',
}

export interface FastOrder {
  orderRefNo: string;
  sentAt: Date;
  originalTariffCode: string; // Pokud položka nemá tariffCode tak productCode
  newTariffCode: string; // Pokud položka nemá tariffCode tak productCode
  fastOrderName: string; // Název požadavku (čeho se to týká) např 'Automatická obnova dat',
  fastOrderType: FastOrderType;
  customerAccountId: string;
  category?: string;
}

export enum ProductGroup {
  mobileVoice = 'mobileVoice',
  internet = 'internet',
  tv = 'tv',
  other = 'other',
  hw = 'hw',
}

export enum UpsellComponent {
  'sidesheet' = 'tariffChangeUpsell.sidesheet',
}

export interface UpsellSummary {
  upsellComponent: UpsellComponent;
  productGroup: ProductGroup;
  /** which product code is being changed */
  productCode: string;
  /** which product's assetRefNo is being changed */
  assetRefNo: string;
  products: Product[];
}

export interface UpsellOrderSubmitSuccess {
  upsellComponent: UpsellComponent;
  productGroup: ProductGroup;
  /** which product code is being changed */
  productCode: string;
  /** which product's assetRefNo is being changed */
  assetRefNo: string;
  products: Product[];
  order: Order;
}

export interface ButtonOptions {
  productGroup: ProductGroup;
  productDetail: { productCode: string; assetRefNo: string };
}

export interface TrackDeeplinkJson {
  [key: `analytics_${string}`]: string;
}
